import { ellipsesInMiddle } from "@me8eon/string_utils";
import { AiToolImpl } from "@me8eon/aiclient";
import { ElasticSearchConfig, ElasticSearchKnnSearchResponse } from "@me8eon/ai.tools.utils";
import { ElasticsearchNexthinkDocument } from "./model/elasticsearchResponse";
import { checkIfWindows } from "@me8eon/browser";
import { EnvironmentConfig } from "@me8eon/config";
import { JwtTokenFn } from "@me8eon/authentication";

const NEXTHINK_EXCLUSION_PATTERNS = {
    bootAndBiosIssues: [
        /boot\s*loop/i,
        /boot-loop/i,
        /stuck\s*(on|at)\s*(boot|bios)/i,
        /doesn'?t?\s*boot/i,
        /won'?t?\s*boot/i,
        /not\s*boot(ing)?/i,
        /fail(s|ed)?\s*to\s*boot/i,
        /stuck\s*(on|at)\s*(hp|dell|lenovo|asus|acer|microsoft|manufacturer)\s*(logo|screen)/i,
        /(hp|dell|lenovo|asus|acer)\s*(logo|screen).*stuck/i,
        /black\s*screen\s*(before|at|on)\s*(windows|login|boot)/i,
        /black\s*screen.*doesn'?t?\s*(reach|get\s*(to|past))/i,
        /doesn'?t?\s*(reach|get\s*(to|past))\s*(the\s*)?(login|windows|desktop)/i,
        /not\s*reaching\s*(login|windows|desktop)/i,
        /bios\s*(issue|problem|update|corrupt|failure)/i,
        /corrupt(ed)?\s*bios/i,
        /firmware\s*(issue|problem|update|corrupt|failure)/i,
        /laptop\s*doesn'?t?\s*(open|start|turn\s*on)/i,
        /laptop\s*(won'?t?|will\s*not)\s*(open|start|turn\s*on)/i,
        /device\s*doesn'?t?\s*(open|start|boot)/i,
        /automatically\s*restarts?\s*(before|at|on)\s*(login|windows)/i,
        /restarts?\s*(on|at|before)\s*(boot|startup|logo)/i,
        /keeps?\s*restart(ing|s)?\s*(before|at)\s*(login|windows)/i,
        /restart(s|ing)?\s*(2|3|multiple|several)\s*times?\s*before/i,
        /not\s*(a|an)?\s*windows\s*update/i,
        /isn'?t?\s*(a|an)?\s*windows\s*update/i,
        /laptop\s*(is\s*)?not\s*opening/i,
        /screen\s*(is\s*)?black.*turns?\s*(on|off)/i,
        /black\s*(screen)?.*until\s*i\s*stop/i,
        /not\s*booting\s*up/i,
        /doesn'?t?\s*(even\s*)?(reach|get\s*to)\s*(the\s*)?login/i,
        /(hp|dell)\s*logo.*failed/i,
        /(hp|dell)\s*logo.*reboots?/i,
        /stuck\s*(on\s*)?(a\s*)?(hp|dell)\s*logo/i,
        /it\s*automatically\s*restarts/i,
        /stuck\s*on\s*a\s*black\s*screen/i,
        /stuck\s*on\s*(a\s*)?black\s*screen/i,
        /is\s*not\s*reaching\s*(the\s*)?login/i,
        /initialize\s*(the\s*)?windows/i,
        /doesn'?t?\s*(even\s*)?initialize/i,
        /bios\s*(might\s*)?(got\s*)?corrupt/i,
        /restarts?\s*like\s*\d+\s*\d*\s*times/i,
        /reboots?.*doesn'?t?\s*get\s*past/i,
        /won'?t?\s*open\s*anymore/i,
        /\bblue\s*screen\b/i,
        /\bbsod\b/i,
        /\bboot\b.*\b(fail(ed)?|failure|error|crash|stuck|loop)\b/i,
        /\b(won't|can't|cannot|doesn't|does\s*not)\s*boot\b/i,
        /\bstartup\b.*\b(fail(ed)?|failure|error|crash)\b/i,
        /\bwindows\b.*\b(fail(ed)?|crash(ed|es)?)\s*(to\s*)?\b(boot|start|load)\b/i,
    ],

    teamsCameraAndVideo: [
        /\bteams\b.*\b(camera|webcam|video)\b/i,
        /\b(camera|webcam|video)\b.*\bteams\b/i,
        /\bexternal\s*camera\b.*\bteams\b/i,
        /\bteams\b.*\bexternal\s*camera\b/i,
        /\bcamera\b\s*(is\s*)?(on\s*)?but\s*(is\s*)?(black|blank)/i,
        /\bwebcam\b\s*(works?|fine)\s*(in|on)\s*other\s*apps?\s*but\s*not\s*(in\s*)?\bteams\b/i,
        /\bvideo\b\s*(is\s*)?(blurry|low\s*quality|pixelat)/i,
        /\bteams\b\s*(keeps?\s*)?asking\s*(for\s*)?\bcamera\b\s*permission/i,
        /\bcamera\b\s*permission.*\bteams\b/i,
        /see\s*myself\s*but\s*(no\s*one|nobody|others)\s*(else\s*)?can/i,
        /\bvideo\b\s*preview\s*works?.*but.*doesn'?t?\s*(work|show)/i,
        /\bcamera\b\s*turns?\s*off\s*auto/i,
        /(can'?t?|cannot)\s*see\s*my\s*(external\s*)?\bcamera\b/i,
    ],

    teamsAudioAndMic: [
        /\bteams\b.*\b(microphone|mic|headset|audio)\b/i,
        /\b(microphone|mic|headset|audio)\b.*\bteams\b/i,
        /nobody\s*(can\s*)?hear(s)?\s*me/i,
        /no\s*one\s*(can\s*)?hear(s)?\s*me/i,
        /can'?t?\s*hear\s*me/i,
        /they\s*can'?t?\s*hear\s*me/i,
        /\bheadset\b\s*(is\s*)?(not\s*)?working\s*(on|in|with)\s*\bteams\b/i,
        /\bmic\b\s*(works?|fine)\s*(in|on)\s*other\s*apps?\s*but\s*not\s*(in\s*)?\bteams\b/i,
        /\bmicrophone\b\s*(works?|fine)\s*(in|on)\s*other\s*apps?\s*but\s*not/i,
        /\bteams\b\s*(says?|shows?)\s*(my\s*)?\bmic(rophone)?\b\s*(is\s*)?muted/i,
        /muted\s*even\s*though\s*(it'?s?\s*)?not/i,
        /using\s*(my\s*)?(laptop|built-?in)\s*\bmic\b\s*instead\s*of\s*(my\s*)?\bheadset\b/i,
        /can'?t?\s*unmute/i,
        /cannot\s*unmute/i,
        /can\s*hear\s*(people|them|others)\s*but\s*they\s*can'?t?\s*hear/i,
        /noise\s*suppression\s*(not\s*)?working/i,
        /keeps?\s*switch(ing)?\s*\baudio\b\s*devices?/i,
        /\bteams\b\s*(keeps?\s*)?mute(s|ing)?\s*(my\s*)?\bmic\b/i,
        /\baudio\b\s*device\s*switch/i,
    ],

    vagueUnclearQueries: [
        /^(my\s*)?\bteams\b\s*(is\s*)?(having|has)\s*(some\s*)?(issues?|problems?|trouble)\.?$/i,
        /^(my\s*)?\blaptop\b\s*(is\s*)?(having|has)\s*(some\s*)?(issues?|problems?|trouble)\.?$/i,
        /^(my\s*)?\bcomputer\b\s*(is\s*)?(having|has)\s*(some\s*)?(issues?|problems?|trouble)\.?$/i,
        /^(my\s*)?\bpc\b\s*(is\s*)?(having|has)\s*(some\s*)?(issues?|problems?|trouble)\.?$/i,
        /^(my\s*)?\bdevice\b\s*(is\s*)?(having|has)\s*(some\s*)?(issues?|problems?|trouble)\.?$/i,
        /^(my\s*)?\boutlook\b\s*(is\s*)?(having|has)\s*(some\s*)?(issues?|problems?|trouble)\.?$/i,
        /^(something|there)\s*(is\s*)?wrong\s*with\s*(my\s*)?\b(teams|laptop|computer|pc|device|outlook)\b\.?$/i,
        /^(my\s*)?\b(teams|laptop|computer|pc|device|outlook)\b\s*(is\s*)?(not\s*working|broken|acting\s*up|acting\s*weird)\.?$/i,
        /^(i\s*(have|got|am\s*having)\s*)?(issues?|problems?|trouble)\s*with\s*(my\s*)?\b(teams|laptop|computer|pc|device|outlook)\b\.?$/i,
        /^help\s*(me\s*)?with\s*(my\s*)?\b(teams|laptop|computer|pc|device|outlook)\b\.?$/i,
        /^(my\s*)?\b(teams|laptop|computer|pc|device|outlook)\b\s*needs?\s*(help|fixing|to\s*be\s*fixed)\.?$/i,
    ],
    browserHomepage: [
        /\b(browser|chrome|edge|firefox)\s*homepage\b/i,
        /\bchange\s*(my\s*)?homepage\b/i,
        /\bhomepage\s*(settings|changed|hijacked)\b/i,
        /\bbrowser\s*(starts|opens)\s*(wrong|unwanted|different)\s*page\b/i,
        /\bcan'?t\s*set\s*preferred\s*homepage\b/i,
        /\bhomepage\s*revert(s|ing)\b/i,
        /\bbrowser\s*reset(s|ting)\s*default\s*homepage\b/i,
        /\b(default|new\s*tab|unwanted)\s*page\s*on\s*startup\b/i,
        /\bcan'?t\s*change\s*homepage\s*in\s*settings\b/i,
        /\bhomepage\s*not\s*saving\b/i,
    ]
};

function shouldSkipNexthink(query: string): { shouldSkip: boolean; reason: string | null } {
    for (const [category, patterns] of Object.entries(NEXTHINK_EXCLUSION_PATTERNS)) {
        for (const pattern of patterns) {
            if (pattern.test(query)) {
                return { shouldSkip: true, reason: category };
            }
        }
    }
    return { shouldSkip: false, reason: null };
}

const mapElasticSearchDocumentsToString = (documents: ElasticsearchNexthinkDocument[]): string => {
    return documents.map((doc, index) => {
        const descriptionText = doc._source.description || doc._source.keywords || '';
        const docIdWithIndex = `${doc._index}/${doc._id}`;
        return `**Action ${index + 1}**:
            - Title: ${doc._source.title}
            - Description: ${ellipsesInMiddle(descriptionText, 200)}
            - Category: ${doc._source.category}
            - Elasticsearch Document ID: ${docIdWithIndex}
            - Index: ${doc._index}`
    }).join("\n")
}

type ToolArgs = {
    query: string
}

type ToolImpl = AiToolImpl<ToolArgs>

type Tool = (esConfig: ElasticSearchConfig, jwtTokenFn: JwtTokenFn) => ToolImpl


export const FetchNexthinkActionsToolImpl: Tool = (esConfig, jwtTokenFn) => {
    return {
        type: "nextthink",
        toolDefn: {
            type: 'function',
            name: "fetch_nexthink_actions",
            description: "This tool fetches Nexthink remediation actions from Elasticsearch using semantic search. This tool is NOT for browser homepage changes or Intune-managed device configurations. Pass the user's EXACT original query without any modifications, expansions, or added keywords. The semantic search engine will handle relevance matching.",
            parameters: {
                type: "object",
                required: ["query"],
                additionalProperties: false,
                properties: {
                    query: {
                        type: "string",
                        description: "The user's EXACT original query as they typed it. Do NOT add keywords, rephrase, expand, or modify the query in any way. Pass it verbatim.",
                    }
                }
            },
        },
        execute: async (indices, { query }) => {
            const {
                axios,
                blacklistedFields = ["full_text_embeddings", "vectorisation_model"],
            } = esConfig;

            if (!query || query.trim().length === 0) throw new Error("No indicies provided to knn tool")
            if (indices.length === 0) throw new Error("No indicies provided to knn tool");
            if (!checkIfWindows()) {
                return {
                    value: "Nexthink action retrieval is only supported on Windows devices.",
                    _meta: {
                        selectedIndex: null,
                    }
                }
            }

            // PRE-FILTER: Check if query should skip Nexthink entirely
            const skipCheck = shouldSkipNexthink(query);
            if (skipCheck.shouldSkip) {
                return {
                    value: `This query (${skipCheck.reason}) cannot be resolved by Nexthink automated actions. You MUST use the semanticHybridSearch tool to search the knowledge base for manual troubleshooting steps. Do NOT call display_nexthink_action.`,
                    _meta: {
                        selectedIndex: "none",
                        skipNexthink: true,
                        skipReason: skipCheck.reason
                    }
                };
            }

            const top = 3;
            const indiciesAsString = indices.join(",");

            const url = `${EnvironmentConfig.elasticSearchProxyUrl}/${indiciesAsString}/_search`;
            const headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${await jwtTokenFn()}`,
            };

            const body = {
                _source: {
                    excludes: blacklistedFields,
                },
                knn: {
                    field: "full_text_embeddings",
                    query_vector_builder: {
                        text_embedding: {
                            model_id: ".multilingual-e5-small_linux-x86_64",
                            model_text: query,
                        },
                    },
                    k: top,
                    num_candidates: top + 10,
                },
            };

            const response = await axios.post<ElasticSearchKnnSearchResponse>(url, body, { headers: headers });

            const allDocuments = response.data?.hits?.hits as unknown as ElasticsearchNexthinkDocument[];

            const disabledCategories = ['repair-O365-apps'];

            const topResultWasDisabled = allDocuments.length > 0 &&
                disabledCategories.includes(allDocuments[0]._source.category);

            if (topResultWasDisabled) {
                return {
                    value: "No Nexthink actions available for this query. You MUST use the knn tool to search the knowledge base for troubleshooting steps. Do NOT call display_nexthink_action.",
                    _meta: {
                        selectedIndex: "none",
                        skipNexthink: true
                    }
                };
            }

            const topDocuments = allDocuments
                .filter(doc => !disabledCategories.includes(doc._source.category))
                .slice(0, top);

            if (topDocuments.length === 0) {
                return {
                    value: "No Nexthink actions available for this query. You MUST use the knn tool to search the knowledge base for troubleshooting steps. Do NOT call display_nexthink_action.",
                    _meta: {
                        selectedIndex: "none",
                        skipNexthink: true
                    }
                };
            }

            const isTeamsIndex = indiciesAsString.includes('nexthink-teams-prod');
            const teamsDocsCount = topDocuments.filter(doc => doc._index === 'nexthink-teams-prod').length;

            let selectedDocument: ElasticsearchNexthinkDocument;

            if (isTeamsIndex && teamsDocsCount >= 2) {
                const categoryVotes = new Map<string, { count: number, docs: ElasticsearchNexthinkDocument[] }>();
                topDocuments.forEach(doc => {
                    const category = doc._source.category;
                    if (!categoryVotes.has(category)) {
                        categoryVotes.set(category, { count: 0, docs: [] });
                    }
                    const vote = categoryVotes.get(category)!;
                    vote.count++;
                    vote.docs.push(doc);
                });

                if (categoryVotes.has('clear-teams-cache')) {
                    const cacheDocs = categoryVotes.get('clear-teams-cache')!.docs;
                    selectedDocument = cacheDocs.reduce((highest, current) =>
                        current._score > highest._score ? current : highest
                    );
                } else {
                    selectedDocument = topDocuments[0];
                }
            } else {
                const indexVotes = new Map<string, { bestScore: number, doc: ElasticsearchNexthinkDocument }>();
                topDocuments.forEach(doc => {
                    const indexName = doc._index;
                    if (!indexVotes.has(indexName)) {
                        indexVotes.set(indexName, { bestScore: doc._score, doc });
                    } else {
                        const existing = indexVotes.get(indexName)!;
                        if (doc._score > existing.bestScore) {
                            indexVotes.set(indexName, { bestScore: doc._score, doc });
                        }
                    }
                });

                let highestScore = 0;
                selectedDocument = topDocuments[0];
                indexVotes.forEach((vote) => {
                    if (vote.bestScore > highestScore) {
                        highestScore = vote.bestScore;
                        selectedDocument = vote.doc;
                    }
                });
            }

            const topResult = [selectedDocument];
            const selectedIndex = selectedDocument._index;

            const content = `Here are the top ${topResult.length} Nextthink actions found (from index: ${selectedIndex}):\n\n${mapElasticSearchDocumentsToString(topResult)}\n\n**Selected Index**: ${selectedIndex}`;

            return {
                value: content,
                _meta: {
                    selectedIndex,
                }
            }
        }
    }
}